import React, { Suspense, lazy, Component } from "react";

/* ─── Top-level error boundary ───────────────────────────────────────────────
   Catches ANY unhandled render error (localStorage blocked in private mode,
   missing API, unexpected JS crash) so the user never sees a blank screen.
   ChunkLoadError triggers a one-shot reload; everything else shows a fallback.
─────────────────────────────────────────────────────────────────────────── */
class AppErrorBoundary extends Component {
  state = { hasError: false, isChunkError: false };

  static getDerivedStateFromError(err) {
    const isChunk = err?.name === "ChunkLoadError" || (err?.message || "").includes("Loading chunk");
    return { hasError: true, isChunkError: isChunk };
  }

  componentDidCatch(err) {
    if (this.state.isChunkError) {
      // Stale cache after redeploy — reload once to fetch fresh chunks
      if (!sessionStorage.getItem("pt_chunk_reloaded")) {
        sessionStorage.setItem("pt_chunk_reloaded", "1");
        window.location.reload();
      }
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", minHeight: "100vh", gap: "1.25rem",
        padding: "2rem", background: "#f8fafc", textAlign: "center",
      }}>
        <img src="/Images/PEOPLETREE_Logo.png" alt="PeopleTree" style={{ height: 56, marginBottom: 8 }}
          onError={e => { e.target.style.display = "none"; }} />
        <h2 style={{ color: "#2c4259", fontFamily: "Georgia, serif", margin: 0 }}>
          Oops! Page couldn't load
        </h2>
        <p style={{ color: "#6b7280", fontSize: "0.95rem", maxWidth: 360, lineHeight: 1.6 }}>
          This might be a network issue or a temporary glitch. Please check your
          internet connection and try again.
        </p>
        <button
          onClick={() => { sessionStorage.removeItem("pt_chunk_reloaded"); window.location.reload(); }}
          style={{ padding: "0.65rem 1.6rem", background: "#3f5f7d", color: "#fff",
            border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "0.95rem" }}
        >
          Try Again
        </button>
        <a href="/" style={{ color: "#3f5f7d", fontSize: "0.85rem" }}>← Go to Home</a>
      </div>
    );
  }
}

// Keep a named alias so nothing else in the file needs to change
const ChunkErrorBoundary = AppErrorBoundary;
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ToastContainer } from "react-toastify";
import {
  WhatsAppFloat,
  AnnouncementPopup,
  EnquiryPopup,
  ScrollToTopButton,
} from "./components/common/FloatingWidgets";
import ScrollToTop from "./components/common/ScrollToTop";
import "react-toastify/dist/ReactToastify.css";
import "./assets/css/global.css";

import { AuthProvider, useAuth } from "./context/AuthContext";
import { SiteSettingsProvider } from "./context/SiteSettingsContext";

// Always-needed layout pieces (eagerly loaded)
import Header from "./components/common/Header";
import Footer from "./components/common/Footer";

// Critical public pages (eagerly loaded — above-the-fold content)
import Home from "./pages/public/Home";
import About from "./pages/public/About";

// Non-critical public pages (lazy)
const AboutFamily     = lazy(() => import("./pages/public/AboutSub").then(m => ({ default: m.AboutFamily })));
const AboutStory      = lazy(() => import("./pages/public/AboutSub").then(m => ({ default: m.AboutStory })));
const LeadershipPage  = lazy(() => import("./pages/public/LeadershipPage"));
const OrganizationChartPage = lazy(() => import("./pages/public/OrganizationChartPage"));
const CoursesPage     = lazy(() => import("./pages/public/CoursePages").then(m => ({ default: m.CoursesPage })));
const CourseDetail    = lazy(() => import("./pages/public/CoursePages"));
const LifeMain        = lazy(() => import("./pages/public/LifePages").then(m => ({ default: m.LifeMain })));
const Facilities      = lazy(() => import("./pages/public/LifePages").then(m => ({ default: m.Facilities })));
const NewsList        = lazy(() => import("./pages/public/LifePages").then(m => ({ default: m.NewsList })));
const NewsDetail      = lazy(() => import("./pages/public/LifePages").then(m => ({ default: m.NewsDetail })));
const GalleryPage     = lazy(() => import("./pages/public/LifePages").then(m => ({ default: m.GalleryPage })));
const IncubationPage  = lazy(() => import("./pages/public/LifePages").then(m => ({ default: m.IncubationPage })));
const Wings           = lazy(() => import("./pages/public/WingsContact").then(m => ({ default: m.WingsPage })));
const Contact         = lazy(() => import("./pages/public/WingsContact").then(m => ({ default: m.ContactPage })));
const LibraryHome           = lazy(() => import("./pages/public/LibraryPages").then(m => ({ default: m.LibraryHome })));
const LibraryEResources     = lazy(() => import("./pages/public/LibraryPages").then(m => ({ default: m.LibraryEResources })));
const LibraryEJournals      = lazy(() => import("./pages/public/LibraryPages").then(m => ({ default: m.LibraryEJournals })));
const LibraryQuestionPapers = lazy(() => import("./pages/public/LibraryPages").then(m => ({ default: m.LibraryQuestionPapers })));
const LibraryBookCollection = lazy(() => import("./pages/public/LibraryPages").then(m => ({ default: m.LibraryBookCollection })));
const LibraryDeptWise       = lazy(() => import("./pages/public/LibraryPages").then(m => ({ default: m.LibraryDeptWise })));
const LibraryPrintResources     = lazy(() => import("./pages/public/LibraryPages").then(m => ({ default: m.LibraryPrintResources })));
const LibraryPrintResourcesPage = lazy(() => import("./pages/public/LibraryPages").then(m => ({ default: m.LibraryPrintResourcesPage })));
const LibraryStaffPage      = lazy(() => import("./pages/public/LibraryPages").then(m => ({ default: m.LibraryStaffPage })));
const LibraryServicesPage   = lazy(() => import("./pages/public/LibraryPages").then(m => ({ default: m.LibraryServicesPage })));
const LibraryFacilitiesPage = lazy(() => import("./pages/public/LibraryPages").then(m => ({ default: m.LibraryFacilitiesPage })));
const LibraryRulesPage      = lazy(() => import("./pages/public/LibraryPages").then(m => ({ default: m.LibraryRulesPage })));
const LibraryGallery        = lazy(() => import("./pages/public/LibraryPages").then(m => ({ default: m.LibraryGallery })));
const LibraryFeedbackPage   = lazy(() => import("./pages/public/LibraryPages").then(m => ({ default: m.LibraryFeedbackPage })));
const AdmissionPage   = lazy(() => import("./pages/public/AdmissionPage"));
const FacultyListingPage = lazy(() => import("./pages/public/FacultyListingPage"));
const PlacementsPage  = lazy(() => import("./pages/public/PlacementsPage"));
const AmenitiesPage   = lazy(() => import("./pages/public/AmenitiesPage"));
const FeedbackPage    = lazy(() => import("./pages/public/FeedbackPage"));
const AictePage       = lazy(() => import("./pages/public/AictePage"));
const CodeOfConductPage = lazy(() => import("./pages/public/CodeOfConductPage"));
const AlumniPage      = lazy(() => import("./pages/public/AlumniPage"));

// Admin (all lazy — never downloaded by public visitors)
const AdminLogin      = lazy(() => import("./pages/admin/AdminLogin"));
const AdminLayout     = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard  = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminSettings   = lazy(() => import("./pages/admin/AdminSettings"));
const AdminLibrary    = lazy(() => import("./pages/admin/AdminLibrary"));
const AdminResourcePages = lazy(() => import("./pages/admin/AdminResourcePages"));

// Lazy-loaded named admin resource exports
const AdminPageSections   = lazy(() => import("./pages/admin/AdminResourcePages").then(m => ({ default: m.AdminPageSections })));
const AdminHeroSlides     = lazy(() => import("./pages/admin/AdminResourcePages").then(m => ({ default: m.AdminHeroSlides })));
const AdminCourses        = lazy(() => import("./pages/admin/AdminResourcePages").then(m => ({ default: m.AdminCourses })));
const AdminFaculty        = lazy(() => import("./pages/admin/AdminResourcePages").then(m => ({ default: m.AdminFaculty })));
const AdminLeadership     = lazy(() => import("./pages/admin/AdminResourcePages").then(m => ({ default: m.AdminLeadership })));
const AdminNews           = lazy(() => import("./pages/admin/AdminResourcePages").then(m => ({ default: m.AdminNews })));
const AdminGalleries      = lazy(() => import("./pages/admin/AdminResourcePages").then(m => ({ default: m.AdminGalleries })));
const AdminTestimonials   = lazy(() => import("./pages/admin/AdminResourcePages").then(m => ({ default: m.AdminTestimonials })));
const AdminDepartments    = lazy(() => import("./pages/admin/AdminResourcePages").then(m => ({ default: m.AdminDepartments })));
const AdminRecruiters     = lazy(() => import("./pages/admin/AdminResourcePages").then(m => ({ default: m.AdminRecruiters })));
const AdminMilestones     = lazy(() => import("./pages/admin/AdminResourcePages").then(m => ({ default: m.AdminMilestones })));
const AdminFacilities     = lazy(() => import("./pages/admin/AdminResourcePages").then(m => ({ default: m.AdminFacilities })));
const AdminWings          = lazy(() => import("./pages/admin/AdminResourcePages").then(m => ({ default: m.AdminWings })));
const AdminIncubation     = lazy(() => import("./pages/admin/AdminResourcePages").then(m => ({ default: m.AdminIncubation })));
const AdminAchievements   = lazy(() => import("./pages/admin/AdminResourcePages").then(m => ({ default: m.AdminAchievements })));
const AdminEnquiries      = lazy(() => import("./pages/admin/AdminResourcePages").then(m => ({ default: m.AdminEnquiries })));
const AdminUsers          = lazy(() => import("./pages/admin/AdminResourcePages").then(m => ({ default: m.AdminUsers })));
const AdminAnnouncements  = lazy(() => import("./pages/admin/AdminResourcePages").then(m => ({ default: m.AdminAnnouncements })));
const AdminPlacements     = lazy(() => import("./pages/admin/AdminResourcePages").then(m => ({ default: m.AdminPlacements })));
const AdminWorkshops      = lazy(() => import("./pages/admin/AdminResourcePages").then(m => ({ default: m.AdminWorkshops })));
const AdminAlumni         = lazy(() => import("./pages/admin/AdminResourcePages").then(m => ({ default: m.AdminAlumni })));
const AdminAlumniOfficers = lazy(() => import("./pages/admin/AdminResourcePages").then(m => ({ default: m.AdminAlumniOfficers })));
const AdminAboutPage      = lazy(() => import("./pages/admin/AdminResourcePages").then(m => ({ default: m.AdminAboutPage })));

const PageLoader = () => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
    <div style={{ width: 40, height: 40, border: "3px solid #e5e7eb", borderTopColor: "#c9a84c", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
  </div>
);

const ProtectedRoute = () => {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  return user ? <Outlet /> : <Navigate to="/admin/login" />;
};

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <SiteSettingsProvider>
          <BrowserRouter>
            <ScrollToTop />
            <ChunkErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public Routes */}
                <Route
                  element={
                    <div>
                      <Header />
                      <main style={{ paddingTop: "var(--header-h, 138px)" }}>
                        <Outlet />
                      </main>
                      <Footer />
                      <WhatsAppFloat />
                      <ScrollToTopButton />
                      <AnnouncementPopup />
                      <EnquiryPopup />
                    </div>
                  }
                >
                  <Route path="/" element={<Home />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/about/family" element={<AboutFamily />} />
                  <Route path="/about/story" element={<AboutStory />} />
                  <Route path="/about/leadership" element={<LeadershipPage />} />
                  <Route path="/about/organization" element={<OrganizationChartPage />} />
                  <Route path="/courses" element={<CoursesPage />} />
                  <Route path="/courses/:slug" element={<CourseDetail />} />
                  <Route path="/life" element={<LifeMain />} />
                  <Route path="/life/facilities" element={<Facilities />} />
                  <Route path="/life/news" element={<NewsList />} />
                  <Route path="/life/news/:slug" element={<NewsDetail />} />
                  <Route path="/life/gallery" element={<GalleryPage />} />
                  <Route path="/life/incubation" element={<IncubationPage />} />
                  <Route path="/wings" element={<Wings />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/library" element={<LibraryHome />} />
                  <Route path="/library/about" element={<Navigate to="/library/about/book-collection" replace />} />
                  <Route path="/library/about/book-collection" element={<LibraryBookCollection />} />
                  <Route path="/library/about/dept-wise" element={<LibraryDeptWise />} />
                  <Route path="/library/about/print-resources" element={<LibraryPrintResourcesPage />} />
                  <Route path="/library/about/print-e-resources" element={<LibraryPrintResources />} />
                  <Route path="/library/about/staff" element={<LibraryStaffPage />} />
                  <Route path="/library/about/services" element={<LibraryServicesPage />} />
                  <Route path="/library/about/facilities" element={<LibraryFacilitiesPage />} />
                  <Route path="/library/about/rules" element={<LibraryRulesPage />} />
                  <Route path="/library/about/question-papers" element={<LibraryQuestionPapers />} />
                  <Route path="/library/about/gallery" element={<LibraryGallery />} />
                  <Route path="/library/about/feedback" element={<LibraryFeedbackPage />} />
                  <Route path="/library/e-resources" element={<LibraryEResources />} />
                  <Route path="/library/e-journals" element={<LibraryEJournals />} />
                  <Route path="/library/question-papers" element={<LibraryQuestionPapers />} />
                  <Route path="/admission" element={<AdmissionPage />} />
                  <Route path="/faculty" element={<FacultyListingPage />} />
                  <Route path="/placements" element={<PlacementsPage />} />
                  <Route path="/amenities" element={<AmenitiesPage />} />
                  <Route path="/feedback" element={<FeedbackPage />} />
                  <Route path="/aicte" element={<AictePage />} />
                  <Route path="/code-of-conduct" element={<CodeOfConductPage />} />
                  <Route path="/alumni" element={<AlumniPage />} />
                </Route>

                {/* Admin Login */}
                <Route path="/admin/login" element={<AdminLogin />} />

                {/* Admin Dashboard */}
                <Route element={<ProtectedRoute />}>
                  <Route element={<AdminLayout />}>
                    <Route path="/admin" element={<AdminDashboard />} />
                    <Route path="/admin/page-sections" element={<AdminPageSections />} />
                    <Route path="/admin/hero-slides" element={<AdminHeroSlides />} />
                    <Route path="/admin/workshops" element={<AdminWorkshops />} />
                    <Route path="/admin/courses" element={<AdminCourses />} />
                    <Route path="/admin/faculty" element={<AdminFaculty />} />
                    <Route path="/admin/leadership" element={<AdminLeadership />} />
                    <Route path="/admin/news" element={<AdminNews />} />
                    <Route path="/admin/galleries" element={<AdminGalleries />} />
                    <Route path="/admin/achievements" element={<AdminAchievements />} />
                    <Route path="/admin/testimonials" element={<AdminTestimonials />} />
                    <Route path="/admin/announcements" element={<AdminAnnouncements />} />
                    <Route path="/admin/departments" element={<AdminDepartments />} />
                    <Route path="/admin/recruiters" element={<AdminRecruiters />} />
                    <Route path="/admin/milestones" element={<AdminMilestones />} />
                    <Route path="/admin/facilities" element={<AdminFacilities />} />
                    <Route path="/admin/wings" element={<AdminWings />} />
                    <Route path="/admin/incubation" element={<AdminIncubation />} />
                    <Route path="/admin/enquiries" element={<AdminEnquiries />} />
                    <Route path="/admin/users" element={<AdminUsers />} />
                    <Route path="/admin/settings" element={<AdminSettings />} />
                    <Route path="/admin/placements" element={<AdminPlacements />} />
                    <Route path="/admin/alumni" element={<AdminAlumni />} />
                    <Route path="/admin/alumni-officers" element={<AdminAlumniOfficers />} />
                    <Route path="/admin/about-page" element={<AdminAboutPage />} />
                    <Route path="/admin/library" element={<AdminLibrary />} />
                  </Route>
                </Route>
              </Routes>
            </Suspense>
            </ChunkErrorBoundary>

            <ToastContainer
              position="top-right"
              autoClose={3000}
              hideProgressBar={false}
              theme="colored"
            />
          </BrowserRouter>
        </SiteSettingsProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
